import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react'
import { Item, Section } from '@react-stately/collections'
import { Modal, useModals } from 'almanac-ui/Modal'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { Text } from 'blaze-ui/Text/Text'
import { IconButton } from 'blaze-ui/IconButton/IconButton'
import { ArrowDown, Trash2 } from 'blaze-ui/icons/20'
import { Voice } from 'blaze-ui/icons'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { FilePlus1 } from 'blaze-ui/icons/20/FilePlus1'
import { Pause } from 'blaze-ui/icons/20/Pause'
import { Play3 } from 'blaze-ui/icons/20/Play3'
import { Input } from 'almanac-ui/TextInputField/Input'
import { Skeleton } from 'components/Skeleton/Skeleton'
import { Spinner } from 'common/Spinner/Spinner'
import { Select } from 'components/Select/Select'
import { VisualSwitch } from 'components/VisualSwitch/VisualSwitch'
import { AvatarPickerModal } from 'components/AvatarPickerModal/AvatarPickerModal'
import { useGenerateAvatarScript } from 'hooks/queries/brandKits/useGenerateAvatarScript'
import { useGenerateVideoScript } from 'hooks/queries/videos/useGenerateVideoScript'
import { useImportMediaFiles } from 'hooks/useImportMediaFiles'
import { useUploadsProject } from 'hooks/useUploadsProject'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useAudioSample } from 'hooks/useAudioSample'
import {
  DEFAULT_VIDEO_CAPTION_STYLE,
  DEFAULT_VIDEO_VOICEOVER_VOICE,
  getVideoCaptionStyleLabel,
  resolveVideoCaptionStyle,
  resolveVideoVoiceoverVoice,
  VIDEO_VOICEOVER_VOICES_FEMALE,
  VIDEO_VOICEOVER_VOICES_MALE,
} from 'screens/BrandKits/components/ContentPreferences/videoVoiceoverCaptionsConstants'
import {
  VIDEO_CAPTION_PREVIEW_URLS,
  VIDEO_VOICE_PREVIEW_URLS,
} from 'screens/BrandKits/components/ContentPreferences/videoVoiceoverCaptionsUtils'
import { getVoiceLabel } from 'screens/BrandKits/components/ContentPreferences/VideoVoiceoverPreference/VideoVoiceoverPreference'
import { VideoCaptionStyleModal } from 'screens/BrandKits/components/ContentPreferences/VideoCaptionStyleModal/VideoCaptionStyleModal'
import { BrandKitAvatar, ReferenceMediaFile, ScriptAngle } from 'types'
import {
  VideoScriptRequestParams,
  VIDEO_SCRIPT_RECOMMENDED_WORD_LENGTH,
  AVATAR_SCRIPT_RECOMMENDED_WORD_LENGTH,
} from 'utils/videoScriptRequest'
import { getVoiceoverScriptSession } from 'hooks/queries/videos/voiceoverScriptSession'
import { countScriptWords } from 'components/Previews/components/VideoPlayer/AudioEditorModal/utils/getRecommendedWordLength'
import Styles from './ScriptSettingsModal.module.scss'

export type ScriptSettingsMode = 'avatar' | 'ugc_video'

const ANGLE_OPTIONS: Array<{ key: ScriptAngle; title: string; description: string }> = [
  { key: 'testimonial', title: 'Testimonial', description: "Avatar shares a customer's success story" },
  { key: 'recommendation', title: 'Recommendation', description: 'Avatar recommends your product or service' },
  { key: 'product_reaction', title: 'Product reaction', description: 'Avatar reacts to a product you attach' },
]

const ANGLE_BY_KEY: Record<string, (typeof ANGLE_OPTIONS)[number]> = Object.fromEntries(
  ANGLE_OPTIONS.map((option) => [option.key, option]),
)

export interface AvatarScriptSettings {
  avatar: BrandKitAvatar | null
  avatarAngle: ScriptAngle
  avatarScript: string
  avatarCaptions: boolean
  avatarCaptionStyle: string | null
  videoVoiceoverVoice: string | null
  productImage: ReferenceMediaFile | null
}

interface ScriptSettingsModalProps {
  value: AvatarScriptSettings
  topic: string
  brandKitId: number
  mode?: ScriptSettingsMode
  brandPlanScheduleId?: number
  videoScriptRequest?: VideoScriptRequestParams | null
  initialScriptToken?: string | null
  defaultCaptionStyle?: string
  /** Brand kit narration voice — used as the default in create flows only. */
  defaultVoiceoverVoice?: string
  autoGenerate?: boolean
  heading?: string
  submitLabel?: string
  generationCost?: number
  isSubmitting?: boolean
  // Emits the current settings + topic; the caller decides what to do (stash,
  // regenerate, …) and closes the modal when appropriate.
  onSubmit: (settings: AvatarScriptSettings, topic: string) => void
}

export const ScriptSettingsModal = ({
  value,
  topic,
  brandKitId,
  mode = 'avatar',
  brandPlanScheduleId,
  videoScriptRequest,
  initialScriptToken,
  defaultCaptionStyle,
  defaultVoiceoverVoice,
  autoGenerate = true,
  heading,
  submitLabel,
  generationCost,
  isSubmitting,
  onSubmit,
}: ScriptSettingsModalProps) => {
  const isAvatarMode = mode === 'avatar'
  const { openModal, closeModal } = useModals()
  const workspaceId = useCurrentWorkspaceId()
  const uploadsProjectId = useUploadsProject(workspaceId).data?.project?.id
  const { uploadImageFilesWithoutLoadingToast } = useImportMediaFiles(false)
  const captionsFeatureEnabled = useFlipperFlag('video_voiceover_captions')

  const [avatar, setAvatar] = useState<BrandKitAvatar | null>(value.avatar)
  const [angle, setAngle] = useState<ScriptAngle>(value.avatarAngle)
  const [script, setScript] = useState(value.avatarScript)
  const [captions, setCaptions] = useState(value.avatarCaptions)
  const [captionStyle, setCaptionStyle] = useState(
    resolveVideoCaptionStyle(value.avatarCaptionStyle ?? defaultCaptionStyle ?? DEFAULT_VIDEO_CAPTION_STYLE),
  )
  const [voiceoverVoice, setVoiceoverVoice] = useState(() =>
    resolveVideoVoiceoverVoice(value.videoVoiceoverVoice ?? defaultVoiceoverVoice ?? DEFAULT_VIDEO_VOICEOVER_VOICE),
  )
  const [productImage, setProductImage] = useState<ReferenceMediaFile | null>(value.productImage)
  const [isUploading, setIsUploading] = useState(false)
  const [topicValue, setTopicValue] = useState(topic)

  const {
    mutateAsync: generateAvatarScript,
    isLoading: isGeneratingAvatar,
    error: avatarScriptError,
  } = useGenerateAvatarScript()
  const {
    mutateAsync: generateVideoScript,
    isLoading: isGeneratingVideo,
    error: videoScriptError,
  } = useGenerateVideoScript()
  const { playingKey, toggle: toggleSample } = useAudioSample()

  const isGenerating = isAvatarMode ? isGeneratingAvatar : isGeneratingVideo
  const scriptError = isAvatarMode ? avatarScriptError : videoScriptError
  const scriptWordCount = countScriptWords(script)
  const recommendedWordLength = isAvatarMode
    ? AVATAR_SCRIPT_RECOMMENDED_WORD_LENGTH
    : VIDEO_SCRIPT_RECOMMENDED_WORD_LENGTH
  const isOverRecommendedWordLength = scriptWordCount > recommendedWordLength

  const needsProduct = angle === 'product_reaction'
  const missingProduct = needsProduct && !productImage
  const avatarPhotoUrl = avatar?.photo_url ?? null
  const avatarSampleUrl = avatar?.voice?.sample_audio_url ?? null
  const avatarPlaying = !!avatarSampleUrl && playingKey === avatarSampleUrl
  const voicePreviewUrl = VIDEO_VOICE_PREVIEW_URLS[voiceoverVoice] ?? null
  const voicePlaying = !!voicePreviewUrl && playingKey === voicePreviewUrl

  const runAvatarGeneration = useCallback(
    async (generationAngle: ScriptAngle, productUrl: string | null) => {
      if (!avatar) return
      try {
        const generated = await generateAvatarScript({
          brandKitId,
          avatarId: avatar.id,
          angle: generationAngle,
          videoTopic: topicValue,
          productReferenceUrl: productUrl,
        })
        setScript(generated)
      } catch (err) {
        void err
      }
    },
    [avatar, brandKitId, generateAvatarScript, topicValue],
  )

  const runVideoGeneration = useCallback(
    async (existingToken?: string | null) => {
      if (!videoScriptRequest) return
      try {
        const generated = await generateVideoScript({
          ...videoScriptRequest,
          topic: topicValue,
          existingToken,
        })
        setScript(generated)
      } catch (err) {
        void err
      }
    },
    [generateVideoScript, topicValue, videoScriptRequest],
  )

  const runGeneration = useCallback(
    (existingToken?: string | null) => {
      if (isAvatarMode) {
        void runAvatarGeneration(angle, needsProduct ? productImage?.source_url ?? productImage?.url ?? null : null)
        return
      }
      void runVideoGeneration(existingToken)
    },
    [angle, isAvatarMode, needsProduct, productImage, runAvatarGeneration, runVideoGeneration],
  )

  const autoGenerated = useRef(false)
  useEffect(() => {
    if (!autoGenerate || autoGenerated.current || script) return
    if (isAvatarMode) {
      if (!avatar || !topicValue) return
      autoGenerated.current = true
      runGeneration()
      return
    }
    if (!brandPlanScheduleId || !videoScriptRequest || !topicValue) return

    const cachedSession = initialScriptToken ? getVoiceoverScriptSession(initialScriptToken) : null
    if (cachedSession?.status === 'ready') {
      autoGenerated.current = true
      setScript(cachedSession.script)
      return
    }

    autoGenerated.current = true
    runGeneration(initialScriptToken)
  }, [
    autoGenerate,
    script,
    avatar,
    topicValue,
    isAvatarMode,
    brandPlanScheduleId,
    videoScriptRequest,
    initialScriptToken,
    runGeneration,
  ])

  const handleRegenerate = () => {
    runGeneration(isAvatarMode ? undefined : null)
  }

  const handleProductFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    setIsUploading(true)
    try {
      const uploaded = await uploadImageFilesWithoutLoadingToast([file], uploadsProjectId)
      const mediaFile = uploaded?.[0]
      if (mediaFile) {
        setProductImage({
          id: mediaFile.id,
          key: mediaFile.key,
          url: mediaFile.url,
          source_url: mediaFile.source_url,
          thumbnail_url: mediaFile.thumbnail_url,
        })
      }
    } finally {
      setIsUploading(false)
    }
  }

  const renderProductUpload = () => {
    if (isUploading) {
      return (
        <div className={Styles.productUploading} data-testid="product-upload-loading">
          <Spinner variant="dark" />
          <Text variant="secondary">Uploading…</Text>
        </div>
      )
    }

    if (productImage) {
      return (
        <div className={Styles.productRow}>
          <img className={Styles.productThumb} src={productImage.url} alt="" />
          <label className={Styles.productReplace}>
            <Text variant="secondary">Replace</Text>
            <input
              type="file"
              accept="image/jpeg,image/png"
              onChange={handleProductFileChange}
              aria-label="Replace product image"
              hidden
            />
          </label>
          <IconButton
            icon={Trash2}
            size="sm"
            variant="tertiary"
            aria-label="Remove product image"
            onPress={() => setProductImage(null)}
          />
        </div>
      )
    }

    return (
      <label className={Styles.productAttach}>
        <FilePlus1 size={16} />
        <Text variant="secondary">Add product image</Text>
        <input
          type="file"
          accept="image/jpeg,image/png"
          onChange={handleProductFileChange}
          aria-label="Add product image"
          hidden
        />
      </label>
    )
  }

  const handleChangeAvatar = () => {
    openModal(AvatarPickerModal, {
      brandKitId,
      selectedAvatarId: avatar?.id,
      onSelect: setAvatar,
    })
  }

  const currentSettings = (): AvatarScriptSettings => ({
    avatar,
    avatarAngle: angle,
    avatarScript: script,
    avatarCaptions: captions,
    avatarCaptionStyle: captionStyle,
    videoVoiceoverVoice: voiceoverVoice,
    productImage: needsProduct ? productImage : null,
  })

  const renderAngleValue = () => {
    const option = ANGLE_BY_KEY[angle]
    return (
      <>
        <span className={Styles.angleValueTitle}>{option?.title}</span>
        <span className={Styles.angleValueDesc}>{option?.description}</span>
      </>
    )
  }

  return (
    <Modal.Root size="lg" blaze onPressOutside={closeModal} aria-label={heading ?? 'Script & Settings'}>
      <Modal.Header>
        <Heading level={2}>{heading ?? 'Script & Settings'}</Heading>
      </Modal.Header>
      <Modal.Content>
        <div className={Styles.layout}>
          <div className={Styles.scriptColumn}>
            <div className={Styles.sectionHeader}>
              <Heading level={3}>Script Inputs</Heading>
              <Text variant="secondary" color="var(--dark-60)">
                {isAvatarMode
                  ? 'Angle and topic drive the script. Change either and regenerate to get a new draft.'
                  : 'Topic drives the script. Change it and regenerate to get a new draft.'}
              </Text>
            </div>

            <div className={Styles.inputsBox}>
              <div className={Styles.inputsRow}>
                {isAvatarMode && (
                  <div className={Styles.angleField}>
                    <Text variant="label" color="var(--dark-60)">
                      Angle
                    </Text>
                    <div className={Styles.angleSelect}>
                      <Select
                        aria-label="Angle"
                        variant="secondary"
                        size="md"
                        useFullWidth
                        spanClass={Styles.angleValue}
                        selectedKey={angle}
                        onSelectionChange={(key) => setAngle(key as ScriptAngle)}
                        renderSelectValue={renderAngleValue}
                      >
                        {ANGLE_OPTIONS.map((option) => (
                          <Item key={option.key} textValue={option.title}>
                            <div className={Styles.angleOption}>
                              <Text>{option.title}</Text>
                              <Text variant="secondary" color="var(--dark-60)">
                                {option.description}
                              </Text>
                            </div>
                          </Item>
                        ))}
                      </Select>
                    </div>
                    {needsProduct && <div className={Styles.angleProduct}>{renderProductUpload()}</div>}
                  </div>
                )}

                <div className={Styles.topicField}>
                  <Text variant="label" color="var(--dark-60)">
                    Topic
                  </Text>
                  <Input
                    className={Styles.topicTextarea}
                    isTextarea
                    resizable={false}
                    name="topic"
                    size="lg"
                    aria-label="Topic"
                    placeholder="What should this video be about?"
                    value={topicValue}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setTopicValue(e.target.value)}
                  />
                </div>
              </div>

              <div className={Styles.regenerateRow}>
                <Button
                  variant="secondary"
                  size="sm"
                  endIcon={ArrowDown}
                  isDisabled={
                    isGenerating ||
                    !topicValue.trim() ||
                    (isAvatarMode ? missingProduct || !avatar : !videoScriptRequest)
                  }
                  onPress={handleRegenerate}
                >
                  Regenerate script
                </Button>
              </div>
            </div>

            <Heading level={3}>Generated script</Heading>
            {isGenerating ? (
              <Skeleton active paragraph={{ rows: 4 }} title={false} className={Styles.scriptSkeleton} />
            ) : (
              <Input
                className={Styles.scriptTextarea}
                isTextarea
                name="script"
                size="lg"
                placeholder="Write a script, or generate one from the topic"
                value={script}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setScript(e.target.value)}
              />
            )}
            {!isGenerating && scriptError instanceof Error && (
              <Text variant="secondary" color="var(--red-50)">
                {scriptError.message}
              </Text>
            )}
            {!isGenerating && (
              <div className={Styles.duration}>
                <Text variant="secondary" color={isOverRecommendedWordLength ? 'var(--red-50)' : 'var(--dark-40)'}>
                  {scriptWordCount} / {recommendedWordLength} recommended max words
                </Text>
              </div>
            )}
          </div>

          <div className={Styles.divider} />

          <div className={Styles.videoStyleColumn}>
            <div className={Styles.sectionHeader}>
              <Heading level={3}>Video Style</Heading>
              <Text variant="secondary" color="var(--dark-60)">
                How the finished video looks. These don&apos;t change the script.
              </Text>
            </div>

            <div className={Styles.videoStyleCards}>
              {isAvatarMode && (
                <div className={Styles.styleCard}>
                  <div className={Styles.styleThumb}>
                    {avatarPhotoUrl && (
                      <img className={Styles.thumbImg} src={avatarPhotoUrl} alt={avatar?.name ?? ''} />
                    )}
                  </div>
                  <div className={Styles.styleCardBody}>
                    <Text variant="metadata" color="var(--dark-40)">
                      Avatar
                    </Text>
                    <Text variant="secondary" fontWeight="500" lineClamp={1}>
                      {avatar?.name ?? 'No avatar selected'}
                    </Text>
                  </div>
                  <div className={Styles.cardActions}>
                    {avatarSampleUrl && (
                      <IconButton
                        icon={avatarPlaying ? Pause : Play3}
                        size="sm"
                        variant="secondary"
                        aria-label={avatarPlaying ? 'Pause voice sample' : 'Play voice sample'}
                        onPress={() => toggleSample(avatarSampleUrl)}
                      />
                    )}
                    <Button variant="secondary" size="sm" aria-label="Change avatar" onPress={handleChangeAvatar}>
                      Change
                    </Button>
                  </div>
                </div>
              )}

              {!isAvatarMode && captionsFeatureEnabled && (
                <div className={Styles.styleCard}>
                  <div className={`${Styles.styleThumb} ${Styles.voiceThumb}`}>
                    <Voice size={24} color="#8341DF" />
                  </div>
                  <div className={Styles.styleCardBody}>
                    <Text variant="metadata" color="var(--dark-40)">
                      Narration voice
                    </Text>
                    <Text variant="secondary" fontWeight="500" lineClamp={1}>
                      {getVoiceLabel(voiceoverVoice)}
                    </Text>
                  </div>
                  <div className={Styles.cardActions}>
                    {voicePreviewUrl && (
                      <IconButton
                        icon={voicePlaying ? Pause : Play3}
                        size="sm"
                        variant="secondary"
                        aria-label={voicePlaying ? 'Pause voice preview' : 'Play voice preview'}
                        onPress={() => toggleSample(voicePreviewUrl)}
                      />
                    )}
                    <Select
                      aria-label="Narration voice"
                      variant="secondary"
                      size="sm"
                      selectedKey={voiceoverVoice}
                      onSelectionChange={(key) => setVoiceoverVoice(resolveVideoVoiceoverVoice(key as string))}
                      renderSelectValue={() => 'Change'}
                    >
                      <Section title="Female">
                        {VIDEO_VOICEOVER_VOICES_FEMALE.map((option) => (
                          <Item key={option.key} textValue={getVoiceLabel(option.key)}>
                            {getVoiceLabel(option.key)}
                          </Item>
                        ))}
                      </Section>
                      <Section title="Male">
                        {VIDEO_VOICEOVER_VOICES_MALE.map((option) => (
                          <Item key={option.key} textValue={getVoiceLabel(option.key)}>
                            {getVoiceLabel(option.key)}
                          </Item>
                        ))}
                      </Section>
                    </Select>
                  </div>
                </div>
              )}

              {captionsFeatureEnabled && (
                <div className={Styles.styleCard}>
                  <div className={`${Styles.styleThumb} ${Styles.captionThumb}`}>
                    {captions ? (
                      <img className={Styles.captionThumbImg} src={VIDEO_CAPTION_PREVIEW_URLS[captionStyle]} alt="" />
                    ) : (
                      <div className={Styles.captionThumbOff} />
                    )}
                  </div>
                  <div className={Styles.styleCardBody}>
                    <Text variant="metadata" color="var(--dark-40)">
                      Caption style
                    </Text>
                    <Text variant="secondary" fontWeight="500">
                      {captions ? getVideoCaptionStyleLabel(captionStyle) : 'No caption'}
                    </Text>
                  </div>
                  <div className={Styles.cardActions}>
                    <div
                      role="switch"
                      aria-checked={captions}
                      aria-label="Captions"
                      tabIndex={0}
                      onClick={() => setCaptions(!captions)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          setCaptions(!captions)
                        }
                      }}
                    >
                      <VisualSwitch active={captions} variant="md" />
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      aria-label="Change caption style"
                      isDisabled={!captions}
                      onPress={() =>
                        openModal(VideoCaptionStyleModal, {
                          selectedStyle: captionStyle,
                          onConfirm: setCaptionStyle,
                        })
                      }
                    >
                      Change
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="right">
          <Button
            variant="primary"
            size="lg"
            isDisabled={
              (isAvatarMode && missingProduct) ||
              isGenerating ||
              isSubmitting ||
              !topicValue.trim() ||
              (!isAvatarMode && !videoScriptRequest)
            }
            onPress={() => onSubmit(currentSettings(), topicValue)}
          >
            <div className={Styles.submitWithCost}>
              {submitLabel ?? 'Save'}
              {!isSubmitting && !!generationCost && generationCost > 0 && (
                <span className={Styles.submitCost}>
                  <Credits /> {generationCost}
                </span>
              )}
            </div>
          </Button>
        </Modal.FooterContent>
      </Modal.Footer>
    </Modal.Root>
  )
}
